import { useMemo } from 'react'
import { parseQuery, useLocation } from '@fs/zion-router'
import { getFamilyTreeId, getRelativeArk, sanitizeQueryParam } from './sourceLinkerUtils'

type SourceLinkerParams = {
  collectionId: string
  hasNoRecord: boolean
  hinting: string
  isAiHint: boolean
  mhid: string
  nextButton: string
  proceedAttach: boolean
  recordId: string
  sourceType: string
  treePersonId?: string
}

/**
 * Reads the source linker's query string params. Both the page and its loading skeleton need these,
 * and the page itself is loaded lazily, so the parsing lives here rather than inside the page.
 * @returns the sanitized params, with the record and tree person already in the shape the linker wants
 */
export default function useSourceLinkerParams(): SourceLinkerParams {
  const { search } = useLocation()

  return useMemo(() => {
    const parsedQuery = parseQuery(search?.replace(/^\?/, '') ?? '')
    const ark = sanitizeQueryParam(parsedQuery.ark || parsedQuery.pal)
    const mhid = sanitizeQueryParam(parsedQuery.mhid)

    return {
      collectionId: sanitizeQueryParam(parsedQuery.collectionId),
      hasNoRecord: !ark && !mhid,
      hinting: sanitizeQueryParam(parsedQuery.hinting),
      isAiHint: 'aihint' in parsedQuery,
      mhid,
      nextButton: sanitizeQueryParam(parsedQuery.nextButton),
      proceedAttach: 'proceedAttach' in parsedQuery,
      // Format the ark parameter to always be a relative ark that is shaped the same
      recordId: (ark ? getRelativeArk(ark) : '') || mhid,
      sourceType: sanitizeQueryParam(parsedQuery.sourceType),
      // Format the id parameter to support loading with 4:1 arks as an id parameter
      treePersonId: getFamilyTreeId(sanitizeQueryParam(parsedQuery.id)),
    }
  }, [search])
}
